<ng-template #tableHeader>
  <thead fd-table-header [ngClass]="{ sticky: stickyHeaders }">
    <tr fd-table-row class="atom-table-row">
      @if (!!expandableContent) {
        <th
          fd-table-cell
          [style.top]="inverseTranslation"
          [width]="'60px'"
        ></th>
      }
      @for (
        col of columns | showColumns: displayedColumns;
        track identifyColumn($index, col)
      ) {
        <th
          fd-table-cell
          app-interactive-th
          [style.top]="inverseTranslation"
          [class]="'table-header cell-' + col.header"
          [ascending]="ascending"
          [columnTitle]="col.title || col.header"
          [activable]="col.isSortable || col.isFilterable"
          [hoverable]="col.isSortable || col.isFilterable"
          [isSortable]="col.isSortable"
          [isFilterable]="col.isFilterable"
          [isFilterIconVisible]="col.isFilterIconVisible"
          (sort)="sort($event)"
          (filter)="filter($event, col.header)"
          [property]="col.value"
          [currentSortProperty]="sortProperty"
          [currentFilters]="filterItems"
          [sortFn]="col.sortFn"
        ></th>
      }
    </tr>
  </thead>
</ng-template>

<ng-template #tableItem let-row="row">
  <app-expandable-row
    #wrapper
    [clickable]="clickable"
    (rowClick)="onClickRow(row)"
    [isResourcesTable]="this.isResourcesTable"
    [isSelected]="isSelected(row)"
    [cellsContent]="cellsContent"
    [expandContent]="expandableContent"
  ></app-expandable-row>
  <ng-template #cellsContent>
    @for (
      col of columns | showColumns: displayedColumns;
      track trackByColumn($index, col);
      let first = $first
    ) {
      <td
        fd-table-cell
        [key]="col.header"
        [ngClass]="'cell-value cell-' + col.header"
        width="{{ col.width || 'auto' }}"
      >
        @if (first) {
          <div class="cell-content">
            @if (hasChildren(row)) {
              <button
                fd-button
                class="child-button"
                [glyph]="
                  isChildRowExpanded(row)
                    ? 'slim-arrow-down'
                    : 'slim-arrow-right'
                "
                (click)="toggleChildRow(row, $event)"
              ></button>
            }
            @if (!col.bodyTemplate) {
              <span class="text-item">{{ row | nestedValue: col.value }}</span>
            }
            @if (!!col.bodyTemplate) {
              <ng-template
                [ngTemplateOutlet]="col.bodyTemplate"
                [ngTemplateOutletContext]="{ row: row }"
              >
              </ng-template>
            }
          </div>
        }
        @if (!first) {
          @if (!col.bodyTemplate) {
            <span class="text-item">{{ row | nestedValue: col.value }}</span>
          }
          @if (!!col.bodyTemplate) {
            <ng-template
              [ngTemplateOutlet]="col.bodyTemplate"
              [ngTemplateOutletContext]="{ row: row }"
            ></ng-template>
          }
        }
      </td>
    }
  </ng-template>
  <ng-content
    *ngTemplateOutlet="wrapper.template; context: { row: row }"
  ></ng-content>
</ng-template>

<ng-template #tableFoot>
  @if (!loading && !dataset?.length) {
    <tr fd-table-row class="atom-table-row">
      <td fd-table-cell [noData]="true" class="fill-height no-data-message">
        @if (!!this.noDataMessageTemplate) {
          <ng-template
            [ngTemplateOutlet]="this.noDataMessageTemplate"
          ></ng-template>
        }
        @if (!this.noDataMessageTemplate) {
          {{ noDataMessage }}
        }
      </td>
    </tr>
  }
  @if (
    !loading &&
    !!dataset &&
    dataset.length > 0 &&
    (
      dataset
      | filterByFilterItems: filterItems
      | dynamicPipe: filterPipe : filterPipeArgs
    )?.length === 0
  ) {
    <tr fd-table-row class="atom-table-row">
      <td fd-table-cell [noData]="true" class="fill-height">
        {{ noDataFilteredMessage }}
      </td>
    </tr>
  }
  @if (loading && displayLoadingText && !dataset?.length) {
    <tr fd-table-row class="atom-table-row">
      <td fd-table-cell [noData]="true" class="fill-height">
        {{ displayLoadingText }}
      </td>
    </tr>
  }
  @if (displayFooterText) {
    <tr fd-table-row class="atom-table-row">
      <td fd-table-cell [noData]="true">{{ displayFooterText }}</td>
    </tr>
  }
</ng-template>

<ng-template #childrenRows let-row="row">
  @if (hasChildren(row) && isChildRowExpanded(row)) {
    @for (childItem of row.children; track childItem) {
      <app-expandable-row
        #childrenWrapper
        [clickable]="clickable"
        (rowClick)="onClickRow(childItem)"
        [isResourcesTable]="this.isResourcesTable"
        [isSelected]="isSelected(childItem)"
        [cellsContent]="childItemCellsContent"
        [expandContent]="expandableContent"
        [row]="childItem"
      >
      </app-expandable-row>
      <ng-template #childItemCellsContent>
        @for (
          col of columns | showColumns: displayedColumns;
          track trackByColumn($index, col);
          let first = $first
        ) {
          <td
            fd-table-cell
            [key]="col.header"
            [ngClass]="'cell-value cell-' + col.header"
            width="{{ col.width || 'auto' }}"
          >
            <div [class.child-padding]="first" class="cell-content">
              @if (first) {
                <span class="child-indent"></span>
              }
              @if (!col.bodyTemplate) {
                <span class="text-item">{{
                  childItem | nestedValue: col.value
                }}</span>
              }
              @if (!!col.bodyTemplate) {
                <ng-template
                  [ngTemplateOutlet]="col.bodyTemplate"
                  [ngTemplateOutletContext]="{ row: childItem }"
                >
                </ng-template>
              }
            </div>
          </td>
        }
      </ng-template>
      <ng-content
        *ngTemplateOutlet="
          childrenWrapper.template;
          context: { row: childItem }
        "
      ></ng-content>
    }
  }
</ng-template>

@if (virtualScrollingEnabled) {
  <cdk-virtual-scroll-viewport
    (scrolledIndexChange)="onScrolledIndexChange($event)"
    [itemSize]="virtualScrollingItemSize"
    #tableViewPort
  >
    <fd-busy-indicator
      [block]="true"
      size="l"
      [loading]="loading"
      ariaLabel="loading"
    >
      <table fd-table class="atom-table" [noBorderY]="true">
        <ng-content *ngTemplateOutlet="tableHeader"></ng-content>
        <tbody #tbody fd-table-body [noBorderY]="true">
          <ng-container
            *cdkVirtualFor="
              let row of pagination
                ? (dataset
                  | filterByFilterItems: filterItems
                  | dynamicPipe: filterPipe : filterPipeArgs
                  | sortBy
                    : ascending
                    : (isClientSideSortingEnabled ? sortProperty : undefined)
                    : sortFn
                  | sortByFixed: ascending : fixedSortingBy
                  | pagination
                    : pagination.currentPage
                    : pagination.itemsPerPage)
                : (dataset
                  | filterByFilterItems: filterItems
                  | dynamicPipe: filterPipe : filterPipeArgs
                  | sortBy
                    : ascending
                    : (isClientSideSortingEnabled ? sortProperty : undefined)
                    : sortFn
                  | sortByFixed: ascending : fixedSortingBy);
              let i = index
            "
          >
            <ng-content
              *ngTemplateOutlet="tableItem; context: { row: row }"
            ></ng-content>
            <ng-container
              *ngTemplateOutlet="childrenRows; context: { row: row }"
            ></ng-container>
          </ng-container>
          <ng-content *ngTemplateOutlet="tableFoot"></ng-content>
        </tbody>
      </table>
    </fd-busy-indicator>
  </cdk-virtual-scroll-viewport>
} @else {
  <fd-busy-indicator
    [block]="true"
    size="l"
    [loading]="loading"
    ariaLabel="loading"
  >
    <table fd-table class="atom-table" [noBorderY]="true">
      <ng-content *ngTemplateOutlet="tableHeader"></ng-content>
      <tbody #tbody fd-table-body [noBorderY]="true">
        @for (
          row of pagination
            ? (dataset
              | filterByFilterItems: filterItems
              | dynamicPipe: filterPipe : filterPipeArgs
              | sortBy: ascending : sortProperty : sortFn
              | sortByFixed: ascending : fixedSortingBy
              | pagination: pagination.currentPage : pagination.itemsPerPage)
            : (dataset
              | filterByFilterItems: filterItems
              | dynamicPipe: filterPipe : filterPipeArgs
              | sortBy: ascending : sortProperty : sortFn
              | sortByFixed: ascending : fixedSortingBy);
          track identifyItem(i, row);
          let i = $index
        ) {
          <ng-content
            *ngTemplateOutlet="tableItem; context: { row: row }"
          ></ng-content>
          <ng-container
            *ngTemplateOutlet="childrenRows; context: { row: row }"
          ></ng-container>
        }
        <ng-content *ngTemplateOutlet="tableFoot"></ng-content>
      </tbody>
      @if (!!pagination) {
        <tfoot fd-table-footer [ngClass]="{ sticky: stickyHeaders }">
          <tr>
            <td colspan="100%">
              <div class="pagination-container">
                <fd-pagination
                  fd-toolbar-item
                  [totalItems]="
                    (
                      dataset
                      | filterByFilterItems: filterItems
                      | dynamicPipe: filterPipe : filterPipeArgs
                      | sortBy: ascending : sortProperty : sortFn
                    )?.length
                  "
                  [itemsPerPage]="pagination.itemsPerPage"
                  [itemsPerPageOptions]="pagination.itemsPerPageOptions"
                  [displayTextTemplate]="customDisplayTextTemplate"
                  [currentPage]="pagination.currentPage"
                  (pageChangeStart)="newPageClicked($event)"
                  (itemsPerPageChange)="itemsPerPageChange($event)"
                  itemsPerPageLabel="Results per page"
                  ariaLabel="Table pagination"
                ></fd-pagination>
                <ng-template #customDisplayTextTemplate let-showing="showing">
                  <label fd-form-label
                    >Showing {{ currentFirstItem() + 1 }}-{{
                      currentFirstItem() +
                        (
                          dataset
                          | filterByFilterItems: filterItems
                          | dynamicPipe: filterPipe : filterPipeArgs
                          | sortBy: ascending : sortProperty : sortFn
                          | pagination
                            : pagination.currentPage
                            : pagination.itemsPerPage
                        )?.length
                    }}
                    of {{ showing.totalCount }} results</label
                  >
                </ng-template>
              </div>
            </td>
          </tr>
        </tfoot>
      }
    </table>
  </fd-busy-indicator>
}
